import { Component, forceUpdate, h, Host, Listen, Prop, State } from '@stencil/core';

import {
  PAYLOAD_TUTORIAL_WHATS_NEW_STEP_0,
  TUTORIAL_NEW_INTERFACE_ID,
  TUTORIAL_WHATS_NEW_ID,
  tutorialSubmit,
} from '@web-components/module-change-group';
import wcShared from '@web-components/shared';

import { NewsService } from '../services/news.service';
import newsStore, { TabsType } from '../stores/news.store';

@Component({
  tag: 'module-news-component',
  styleUrl: 'main-layout.scss',
  shadow: true,
})
export class MainLayout {
  @Prop() readonly iconSize = '20';

  @State() canBeDisplayed = true;
  @State() isVisible: boolean;
  @State() newsCountNotif = 0;
  @State() showTuto = false;
  @State() showBellAnimation = false;

  private panelRef: { close: () => void; open: () => void };

  @Listen('submitNewsTutoEvent')
  submitNewsTutoEventHandler() {
    this.showTuto = false;
    tutorialSubmit(TUTORIAL_WHATS_NEW_ID, PAYLOAD_TUTORIAL_WHATS_NEW_STEP_0);
  }

  componentWillLoad() {
    if (!window['WC_MODULES_COMMON'].includes('news')) {
      this.canBeDisplayed = false;
      return null;
    }
    wcShared.displayComponent('news');

    // Setup news/events notification
    newsStore.unseenNewsCount = wcShared.store.userStartConfig?.whats_new?.unseen_news_count ?? 0;
    newsStore.currentEventCount = wcShared.store.currentEvents?.length ?? 0;
    this.newsCountNotif =
      wcShared.store.userStartConfig?.whats_new?.news_count_since_last_opened ?? 0;

    // Bell animation
    setTimeout(() => {
      this.showBellAnimation = true;
    }, 3000);

    // Show tutorial
    document.addEventListener('showNewsTuto', (e: CustomEvent) => {
      this.showTuto = e.detail;
    });

    const pendingTutorials = wcShared.store.userStartConfig?.stepper_tutorials?.pending?.find(
      (item) => item.id === TUTORIAL_WHATS_NEW_ID,
    );
    const hasProductsTutorials = wcShared.store.userStartConfig?.stepper_tutorials?.pending?.find(
      (item) => item.id === TUTORIAL_NEW_INTERFACE_ID,
    );
    if (
      pendingTutorials &&
      !Boolean(hasProductsTutorials) &&
      pendingTutorials.step === 0 &&
      ['manager3', 'manager4', 'workspace3', 'drive'].includes(window['CURRENT_PROJECT'])
    ) {
      this.showTuto = true;
    }
    wcShared.store.addObserver('theme', () => forceUpdate(this));
  }

  private initAtClick() {
    newsStore.tab = TabsType.Recent;
    this.isVisible = true;

    if (!newsStore.recent?.length) void NewsService.getNews();

    if (this.newsCountNotif !== 0) {
      NewsService.postOpenedNews().then(() => {
        this.newsCountNotif = 0;
      });
    }

    if (this.showTuto) {
      this.submitNewsTutoEventHandler();
    }
  }

  private handleNewsEvent = (event) => {
    newsStore.unseenNewsCount = event.detail.unseenNewsCount;
    if (event.detail.toClose) this.panelRef.close();
    else this.panelRef.open();
  };

  render() {
    if (!this.canBeDisplayed) {
      return null;
    }

    const allNotifCount = newsStore.currentEventCount + this.newsCountNotif;
    return (
      <Host>
        <wc-panel
          class={`theme-${wcShared?.store?.theme}`}
          onOpen={() => this.initAtClick()}
          onClose={() => (this.isVisible = false)}
          ref={(ref) => (this.panelRef = ref)}
        >
          <div
            slot="trigger"
            class={`bell-icon theme-${wcShared?.store?.theme}`}
          >
            <div class="trigger-wrapper">
              <svg
                width={this.iconSize}
                height={this.iconSize}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="currentColor"
                  d="M18.0625 16.0833C18.0625 16.0833 16.9791 14.9167 16.9791 9.5C16.9791 7.66667 16.2291 5.83333 14.8958 4.5C13.7291 3.33333 12.2291 2.58333 10.5625 2.5V0.666667C10.6458 0.25 10.3125 0 9.97913 0C9.64579 0 9.31246 0.25 9.31246 0.666667V2.58333C7.64579 2.75 6.14579 3.41667 4.97913 4.58333C3.64579 5.91667 2.89579 7.66667 2.89579 9.58333C2.89579 14.5833 1.72913 16.1667 1.72913 16.25C1.64579 16.4167 1.64579 16.6667 1.72913 16.9167C1.81246 17.1667 2.06246 17.25 2.31246 17.25H6.89579C7.06246 18 7.47913 18.6667 7.97913 19.0833C8.56246 19.6667 9.31246 20 10.0625 20C10.8125 20 11.5625 19.6667 12.1458 19.0833C12.6458 18.5833 12.9791 17.9167 13.2291 17.25H17.8125C18.0625 17.25 18.3125 17.0833 18.3958 16.8333C18.3125 16.5833 18.3125 16.3333 18.0625 16.0833ZM11.1458 18.1667C10.4791 18.8333 9.56246 18.8333 8.89579 18.1667C8.64579 17.9167 8.47913 17.5833 8.31246 17.1667H11.8125C11.5625 17.5833 11.3958 17.9167 11.1458 18.1667ZM3.31246 15.9167C3.72913 14.8333 4.22913 12.9167 4.22913 9.5C4.22913 8 4.81246 6.5 5.89579 5.41667C6.97913 4.33333 8.39579 3.75 9.97913 3.75C11.5625 3.75 12.9791 4.33333 14.0625 5.41667C15.1458 6.5 15.7291 7.91667 15.7291 9.5C15.7291 12.9167 16.1458 14.8333 16.5625 15.9167H3.31246Z"
                />
              </svg>
              {allNotifCount !== 0 && (
                <span class={{ 'has-events': newsStore.currentEventCount > 0 }}>
                  {allNotifCount}
                </span>
              )}
            </div>
            <div class="tooltip-slot-container">
              <slot name="tooltip"></slot>
            </div>
            {this.showTuto && (
              <wc-tuto
                position="left"
                type="news"
              />
            )}
          </div>
          <news-component
            onHandleNewsEvent={this.handleNewsEvent}
            isVisible={this.isVisible}
            class={`theme-${wcShared?.store?.theme}`}
          />
        </wc-panel>
      </Host>
    );
  }
}
